import React, { useState, Fragment } from 'react';

import {
    Table,
    Checkbox,
    TableRow,
    TableHead,
    TableCell,
    TableBody,
    TableContainer,
    TableSortLabel,
    Box,
} from '@mui/material';

import { Typography } from './Typography';
import TableSkeleton from './table-skeleton';

const dataTableWithAccordionStyles = () => ({
    tableContainer: {
        width: '100%',
        background: (theme) => theme.backgroundColor.primary,
        overflow: 'auto',
        maxHeight: '800px',
        padding: '4px 24px 0px 24px',
        marginBottom: '10px',
        borderRadius: '12px',
        '& .MuiTableCell-root': {
            borderColor: (theme) => (theme.palette.mode === 'dark' ? '#394150' : '#E5E6EB'),
            fontSize: '12px',
        },
        '&::-webkit-scrollbar': {
            width: '0.4rem',
            height: '0.4rem',
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '&::-webkit-scrollbar-thumb': {
            margin: 1,
            borderRadius: '8px',
            backgroundColor: (theme) => theme.primary.dark,
        },
    },
    headerTableCell: {
        p: '12px',
        background: (theme) => theme.backgroundColor.primary,
        borderBottom: (theme) =>
            theme.palette.mode === 'dark' ? '1px solid #394150' : '1px solid #E5E6EB',
        fontWeight: 500,
        fontSize: 14,
        color: '#6C727F',
    },
    tableRow: {
        cursor: 'pointer',
    },
    accordionCell: {
        paddingBottom: 0,
        paddingTop: 0,
        background: (theme) => theme.backgroundColor.primary,
    },
    headerCellLabel: {
        whiteSpace: 'nowrap',
    },
    headerCheckbox: {
        paddingLeft: 2,
        color: (theme) => theme.palette.primary.main,
    },
});

export const DataTableWithAccordion = ({
    data = [],
    sorting = {},
    headers = [],
    selected = [],
    sortOrder = 'asc',
    isLoading = false,
    isSelectable = false,
    sortHandler = () => {},
    showAccordion = () => {},
    showHeaderCheckBox = true,
    handleSelectRow = () => {},
    handleSelectAllRows = () => {},
    isRowIndeterminate = () => false,
}) => {
    const classes = dataTableWithAccordionStyles();

    const [expandedRows, setExpandedRows] = useState({});

    const toggleRowExpansion = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    const handleSort = (columnId) => {
        const isAsc = sorting[columnId] && sortOrder === 'asc';
        const newSortOrder = isAsc ? 'desc' : 'asc';
        sortHandler(columnId, newSortOrder);
    };

    return (
        <Fragment>
            <TableContainer sx={classes.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {isSelectable && (
                                <TableCell
                                    padding="checkbox"
                                    sx={{
                                        ...classes.headerTableCell,
                                        borderRadius: '8px 0 0 8px',
                                    }}
                                >
                                    {showHeaderCheckBox && (
                                        <Checkbox
                                            color="primary"
                                            indeterminate={
                                                selected.length > 0 &&
                                                data.results?.length > selected.length
                                            }
                                            checked={
                                                data.results?.length === selected.length &&
                                                data.results?.length > 0
                                            }
                                            onChange={handleSelectAllRows}
                                        />
                                    )}
                                </TableCell>
                            )}
                            {headers.map((header) => (
                                <TableCell
                                    key={header.id}
                                    align={header.align || 'left'}
                                    sx={{
                                        ...classes.headerTableCell,
                                        verticalAlign: header.verticalAlign || 'middle',
                                        width: header.maxWidth || 'unset',
                                    }}
                                >
                            {header.sort ? (
                                <TableSortLabel
                                    active={sorting[header.id]}
                                    direction={sorting[header.id] ? sortOrder : 'asc'}
                                    onClick={() => handleSort(header.id)}
                                    sx={classes.headerCellLabel}
                                    hideSortIcon={!header.sort}
                                    disabled={isLoading}
                                >
                                    {header.headerComponent || (
                                        <Typography
                                            text={header.label}
                                            customStyles={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: (theme) =>
                                                    header?.changed
                                                        ? 'black'
                                                        : theme.palette.mode === 'dark'
                                                        ? '#6C727F'
                                                        : '#4D5461',
                                            }}
                                        />
                                    )}
                                </TableSortLabel>
                            ) : (
                                <Box sx={{ userSelect: 'text' }}>
                                    {header.headerComponent || (
                                        <Typography
                                            text={header.label}
                                            customStyles={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: (theme) =>
                                                    header?.changed
                                                        ? 'black'
                                                        : theme.palette.mode === 'dark'
                                                        ? '#6C727F'
                                                        : '#4D5461',
                                            }}
                                        />
                                    )}
                                </Box>
                            )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {isLoading ? (
                        <TableBody>
                            {[...Array(8)].map((i, index) => (
                                <TableSkeleton key={index} numberOfColumns={8} />
                            ))}
                        </TableBody>
                    ) : (
                        <TableBody>
                            {data?.results?.length == 0 ? (
                                <TableRow>
                                    <TableCell colSpan={headers.length} align="center">
                                        <Typography variant="body1" text="No data available" />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data?.results?.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <TableRow hover sx={classes.tableRow}>
                                            {isSelectable && (
                                                <TableCell padding="none" sx={{ padding: '10px' }}>
                                                    <Checkbox
                                                        sx={classes.checkbox}
                                                        checked={selected.includes(row.id)}
                                                        indeterminate={
                                                            !selected.includes(row.id) &&
                                                            isRowIndeterminate(row.id)
                                                        }
                                                        onClick={(event) =>
                                                            handleSelectRow(event, row.id)
                                                        }
                                                    />
                                                </TableCell>
                                            )}
                                            {headers.map((header) => (
                                                <TableCell
                                                    key={header.id}
                                                    align={header.align || 'left'}
                                                    sx={{
                                                        verticalAlign:
                                                            header.verticalAlign || 'middle',
                                                    }}
                                                    onClick={() =>
                                                        header.id === 'action' &&
                                                        toggleRowExpansion(row.id)
                                                    }
                                                >
                                                    {header.render
                                                        ? header.render(row)
                                                        : row[header.id] || '-'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {expandedRows?.[row.id] && (
                                            <TableRow>
                                                <TableCell colSpan={12} sx={classes.accordionCell}>
                                                    {showAccordion(row, () =>
                                                        setExpandedRows((prev) => ({
                                                            ...prev,
                                                            [row.id]: false,
                                                        })),
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        </Fragment>
    );
};
